import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, where, orderBy } from 'firebase/firestore';
import { db, appId } from '../config/firebase';

/**
 * Hook for managing opportunities
 */
export const useOpportunities = (userId, isAuthReady) => {
  const [opportunities, setOpportunities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!isAuthReady || !userId || !db) {
      if (isAuthReady && !userId) {
        setOpportunities([]);
      }
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    const opportunitiesPath = `artifacts/${appId}/users/${userId}/opportunities`;
    
    try {
      // Query for non-expired opportunities
      const q = query(
        collection(db, opportunitiesPath),
        where('status', 'in', ['active', 'dismissed', 'tracked']),
        orderBy('detectedAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(
        q,
        (snap) => {
          const data = snap.docs.map(doc => {
            const docData = doc.data();
            return {
              id: doc.id,
              ...docData,
              // Convert Firestore timestamps to Date objects
              detectedAt: docData.detectedAt?.toDate?.() || new Date(docData.detectedAt),
              emailSentAt: docData.emailSentAt?.toDate?.() || null,
              viewedAt: docData.viewedAt?.toDate?.() || null,
              dismissedAt: docData.dismissedAt?.toDate?.() || null,
              addedToTrackedAt: docData.addedToTrackedAt?.toDate?.() || null,
              expiresAt: docData.expiresAt?.toDate?.() || null
            };
          });
          
          setOpportunities(data);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching opportunities:', err);
          setError('Could not fetch opportunities.');
          setIsLoading(false);
        }
      );
      
      return () => {
        if (typeof unsubscribe === 'function') unsubscribe();
      };
    } catch (err) {
      console.error('Error setting up opportunities listener:', err);
      setError('Could not set up opportunities listener.');
      setIsLoading(false);
    }
  }, [userId, isAuthReady]);
  
  return { opportunities, isLoading, error };
};
